import type { ApolloCache } from '@apollo/client';
import { Menu, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import { omit } from 'lodash';
import pluralize from 'pluralize';
import { Fragment, useEffect } from 'react';
import { CgPlayListRemove } from 'react-icons/cg';
import { HiChevronDown } from 'react-icons/hi';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';
import type { RmRec } from '../../graphql/types';
import { useConfirmDialog } from '../../hooks/use-confirm-dialog';
import { useClearColumnRmRecs, useFindRmRecs, useResetRmRecs } from '../../hooks/use-rm-recs';
import type { FindRmRecArgs } from '../../types/__generated__/global-types';

type BtnProps = {
  btnClass: string;
  btnIconClass?: string;
  filters: FindRmRecArgs;
};

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== '';

// Menu items only mount while the menu is open, so register their tooltips then
const RebuildTooltips = () => {
  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);
  return null;
};

export default function BtnResetRecs({ btnClass, btnIconClass = 'h-5 w-5', filters }: BtnProps) {
  const location = useLocation();
  const isReport = location.pathname.includes('report');
  const refetchQueries = isReport ? ['ReportData', 'FindRmRecs'] : ['FindRmRecs'];
  const [resetRecs, { loading }] = useResetRmRecs(refetchQueries);
  const [clearColRecs, { loading: loadingClear }] = useClearColumnRmRecs(refetchQueries);
  const { confirm, dialog } = useConfirmDialog();

  // Clearing applies to all of the hotel's RM Recs, not just the dates on screen
  const { data: recsData } = useFindRmRecs({ filters: { hotelId: filters.hotelId } });
  const recs: (RmRec | null)[] | undefined = recsData?.hotelRmRecs;
  // Until the recs load, leave every option enabled
  const hasRecs = !recs || recs.length > 0;
  const hasRates = !recs || recs.some((r) => hasValue(r?.rate));
  const hasOtherRecs = !recs || recs.some((r) => hasValue(r?.notes));

  // Evict cached RM Recs so the RM Recs dashboard refetches instead of showing stale rows
  const evictRmRecs = (cache: ApolloCache<unknown>) => {
    cache.evict({ fieldName: 'hotelRmRecs' });
    cache.gc();
  };

  const items = [
    {
      name: 'Clear Rates',
      click: () => handleClear('rate', 'Rec Rates'),
      disabled: !hasRates,
      tooltip: 'There are currently no Rec Rates to clear',
    },
    {
      name: 'Clear Other Recs',
      click: () => handleClear('notes', 'Other Recs'),
      disabled: !hasOtherRecs,
      tooltip: 'There are currently no Other Recs to clear',
    },
  ];

  const successMessage = (action: string, count: number) =>
    count > 0 ? `Success! ${action} ${pluralize('Rm Rec', count, true)}` : 'No Rm Recs to clear';

  const handleClear = async (column: string, label: string) => {
    const confirmed = await confirm({
      title: `Clear ${label}?`,
      message: `This will clear the ${label} on all RM Recs for this hotel. This cannot be undone.`,
      confirmLabel: 'Clear',
    });
    if (!confirmed) return;

    toast.promise(
      clearColRecs({
        variables: {
          filters: {
            ...omit(filters, ['startDate', 'endDate']),
          },
          column,
        },
        update: evictRmRecs,
      }),
      {
        pending: `Clearing ${label}...`,
        success: {
          render: ({ data }) =>
            successMessage(`Cleared ${label} on`, data?.data?.clearColumnRmRecs?.length ?? 0),
        },
        error: 'There was an issue, sorry.',
      },
    );
  };

  const handleClick = async () => {
    if (!hasRecs) return;

    const confirmed = await confirm({
      title: 'Reset RM Recs?',
      message: 'This will remove all RM Recs for this hotel. This cannot be undone.',
      confirmLabel: 'Reset',
    });
    if (!confirmed) return;

    toast.promise(
      resetRecs({
        variables: {
          filters: {
            ...omit(filters, ['startDate', 'endDate']),
          },
        },
        update: evictRmRecs,
      }),
      {
        pending: 'Resetting Rm Recs...',
        success: {
          render: ({ data }) => successMessage('Reset', data?.data?.resetRmRecs?.length ?? 0),
        },
        error: 'There was an issue, sorry.',
      },
    );
  };

  const ResetAllBtn = () => {
    return (
      <span
        onClick={handleClick}
        className={btnClass}
        aria-disabled={!hasRecs}
        data-tip={hasRecs ? 'Reset RM Recs' : 'There are currently no RM Recs to reset'}
      >
        <CgPlayListRemove
          aria-hidden='true'
          className={`${loading || loadingClear ? 'animate-spin' : ''} ${btnIconClass} ${
            hasRecs ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
          }`}
        />
      </span>
    );
  };

  return (
    <div className='relative z-30 inline-flex shadow-sm rounded-md'>
      <ResetAllBtn />
      {dialog}
      <Menu as='div' className='-ml-px relative block'>
        <Menu.Button className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50'>
          <span className='sr-only'>Open options</span>
          <HiChevronDown className={btnIconClass} aria-hidden='true' />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items className='origin-top-right absolute right-0 mt-2 -mr-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none'>
            <RebuildTooltips />
            <div className='py-1'>
              {items.map((item) => (
                <Menu.Item key={item.name} disabled={item.disabled}>
                  {({ active }) => (
                    // aria-disabled instead of disabled, so the tooltip still shows on hover
                    <button
                      type='button'
                      onClick={item.disabled ? undefined : item.click}
                      aria-disabled={item.disabled}
                      data-tip={item.disabled ? item.tooltip : undefined}
                      className={clsx(
                        item.disabled
                          ? 'text-gray-400 cursor-not-allowed'
                          : active
                            ? ' text-gray-900'
                            : 'text-gray-700',
                        'block px-4 py-2 text-sm',
                      )}
                    >
                      {item.name}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );
}
